import React, { useState } from 'react';
import { Colors, Text } from 'library';
import { Stack } from 'library/ui';
import { styled } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { format12HourTime, formatMonthDay, formatShortMonthDayYear } from 'shared/Dates';

import ChangeModal from 'parent-portal/subs/ConfirmSubHours/ChangeModal';
import { ClockInModal } from 'parent-portal/jobs/upcoming/BusinessCalendar/ShiftCard/ClockInButton';
import { BasicCard } from './BasicCard';
import { Link } from './Link';
import { WorkerTab } from './WorkerTab';

import type { IHourReport } from 'internal/payments/HourReportModels';
import type { DashboardSummary, IWorkerTab, UnclockedInTimesheetWorker } from '../types';
import { groupBy } from 'shared/arrays';
import { useHistory } from 'react-router';
import { getTotalHours } from 'shared';

interface ExtendedWorkerTab extends IWorkerTab {
    hour_report: IHourReport | null;
    worker: DashboardSummary['timesheets'][0];
}

export function Timesheets({
    dashboardSummary,
    loading,
    small,
    refresh,
}: {
    dashboardSummary: DashboardSummary;
    loading: boolean;
    small: boolean;
    refresh: () => void;
}) {
    const { timesheets, actionable_timesheets: actionableTimesheets } = dashboardSummary;
    const history = useHistory();
    const [changeReport, setChangeReport] = useState<IHourReport | undefined>(undefined);
    const [clockInWorker, setClockInWorker] = useState<DashboardSummary['timesheets'][0] | undefined>(undefined);

    const hasTimesheets = !!timesheets && timesheets.length > 0;
    const groupedTimesheets = groupBy(timesheets, 'location_name');

    const mostTimesheets: UnclockedInTimesheetWorker[] = Object.values(groupedTimesheets).sort(
        (a, b) => b.length - a.length,
    )[0];

    const workers: ExtendedWorkerTab[] = hasTimesheets
        ? mostTimesheets.map((worker: UnclockedInTimesheetWorker) => ({
              user: worker.user,
              profile_image: worker.profile_image,
              ...middleAndBottomText(worker),
              hour_report: worker.hour_report,
              worker,
          }))
        : [];

    const actionRequiredworkers =
        actionableTimesheets?.map((worker) => ({
            user: worker.user,
            profile_image: worker.profile_image,
            ...middleAndBottomText(worker),
            hour_report: worker.hour_report,
            worker,
            business_location_id: worker.business_location_id,
        })) || [];

    const sentHomeWorkers = dashboardSummary.sent_home_workers?.map((worker) => ({
        user: worker.user,
        profile_image: worker.user.profile_picture,
        middle: <Text variant="caption">Sent Home during shift on {formatMonthDay(worker.shift_date)}</Text>,
        business_location_id: worker.business_location.id,
        worker,
    }));

    function middleAndBottomText(worker: DashboardSummary['timesheets'][0]) {
        if (!worker.hour_report || !worker.hour_report.start)
            return {
                middle: (
                    <Text variant="caption" bold>
                        Not clocked in
                    </Text>
                ),
            };

        if (worker.hour_report.end) {
            return {
                middle: (
                    <Text variant="caption" bold>
                        {formatMonthDay(worker.hour_report.start)},{' '}
                        {format12HourTime(worker.hour_report.start, worker.timezone)} -{' '}
                        {format12HourTime(worker.hour_report.end, worker.timezone)}
                    </Text>
                ),
                bottom: worker.hour_report.approved_break ? (
                    <Text variant="caption">{worker.hour_report.approved_break} minute break</Text>
                ) : (
                    <Text variant="caption">No break taken</Text>
                ),
            };
        }

        const clockedIn = {
            middle: (
                <Text variant="caption">
                    <b>Clock In:</b> {formatMonthDay(worker.hour_report.start)},{' '}
                    {format12HourTime(worker.hour_report.start, worker.timezone)}
                </Text>
            ),
        };

        if (worker.hour_report.approved_break) {
            return {
                ...clockedIn,
                bottom: <Text variant="caption">{worker.hour_report.approved_break} minute break</Text>,
            };
        }

        return clockedIn;
    }

    const noDataToShow =
        !hasTimesheets &&
        !actionRequiredworkers.length &&
        !dashboardSummary.sent_home_workers.length &&
        !dashboardSummary.missing_payment_reports?.length;

    return (
        <BasicCard loading={loading} small={small}>
            <Row>
                <Text variant="h1">Timesheets</Text>
                {hasTimesheets && <Link to="/timesheets">View All</Link>}
            </Row>
            {actionRequiredworkers?.length ? (
                <>
                    <Text variant="body2" bold textStyle={{ marginBottom: 15, color: Colors.terraCotta }}>
                        Action Required: Manual Hours Reported
                    </Text>
                    <Text variant="body2" textStyle={{ marginBottom: 10 }}>
                        Hours were reported by workers after they left your center and need your approval.
                    </Text>
                    <Stack gap="sm" align="start">
                        {actionRequiredworkers.map((x) => (
                            <WorkerTab
                                key={x.user.id}
                                worker={x}
                                small={small}
                                onClick={() => history.push(`/timesheets?location=${x.business_location_id}`)}
                                icon={ArrowForward}
                            />
                        ))}
                    </Stack>
                </>
            ) : null}
            {sentHomeWorkers?.length ? (
                <>
                    <Text variant="body2" bold textStyle={{ marginBottom: 15, color: Colors.terraCotta }}>
                        Sent Home Workers
                    </Text>
                    <Text variant="body2" textStyle={{ marginBottom: 10 }}>
                        Workers were sent home and need your approval.
                    </Text>
                    <Stack gap="sm" align="start">
                        {sentHomeWorkers.map((worker) => (
                            <WorkerTab
                                key={worker.user.id}
                                worker={worker}
                                small={small}
                                onClick={() => history.push(`/timesheets?location=${worker.business_location_id}`)}
                                icon={ArrowForward}
                            />
                        ))}
                    </Stack>
                </>
            ) : null}
            {dashboardSummary.missing_payment_reports?.length ? (
                <>
                    <Text variant="body2" bold textStyle={{ marginBottom: 15, color: Colors.terraCotta }}>
                        Missing Payment Reports
                    </Text>
                    <Stack gap="sm" align="start">
                        {dashboardSummary.missing_payment_reports?.map((report) => (
                            <WorkerTab
                                key={report.id}
                                worker={{
                                    ...report,
                                    middle: (
                                        <Text variant="caption">
                                            <b>{formatShortMonthDayYear(report.start_date)}</b> (
                                            {getTotalHours(report).toFixed(2)} hours)
                                        </Text>
                                    ),
                                }}
                                small={small}
                                attention
                                onClick={() => history.push(`/timesheets?location=${report.business_location.id}`)}
                            />
                        ))}
                    </Stack>
                </>
            ) : null}
            {noDataToShow && <Text variant="body2">No time sheets need attention right now! 🎉</Text>}
            {hasTimesheets && (
                <>
                    <Text variant="body2" bold textStyle={{ marginBottom: 10 }}>
                        {mostTimesheets[0].location_name} ({mostTimesheets.length})
                    </Text>
                    <Stack gap="sm" align="start">
                        {workers.map((worker: ExtendedWorkerTab) => (
                            <WorkerTab
                                key={worker.user.id}
                                worker={worker}
                                small={small}
                                onClick={() =>
                                    worker.hour_report
                                        ? setChangeReport({
                                              ...worker.hour_report,
                                              timezone: worker.worker.timezone,
                                          })
                                        : setClockInWorker(worker.worker)
                                }
                            />
                        ))}
                    </Stack>
                </>
            )}
            {changeReport && (
                <ChangeModal
                    changeReport={changeReport}
                    setChangeReport={setChangeReport}
                    updateHoursReported={refresh}
                    fullScreen={small}
                />
            )}
            {clockInWorker && (
                <ClockInModal
                    open={!!clockInWorker}
                    onClose={() => setClockInWorker(undefined)}
                    onComplete={refresh}
                    appointmentId={clockInWorker.appointment_id}
                    startDate={clockInWorker.start_date}
                    providerName={`${clockInWorker.user.first_name} ${clockInWorker.user.last_name}`}
                    providerUserId={clockInWorker.user.id}
                    timezone={clockInWorker.timezone}
                />
            )}
        </BasicCard>
    );
}

const Row = styled('div')({
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    marginBottom: 15,
    justifyContent: 'space-between',
    width: '100%',
});
